import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  createScannerRule,
  deleteScannerRule,
  getScannerDashboard,
  ScannerCondition,
  ScannerDashboard,
  ScannerMarketScope,
  ScannerRule,
  ScannerRulePayload,
  ScannerUniverseScope,
  setScannerRuleEnabled,
  updateScannerRule,
} from '../../api/scannerApi';

type DraftCondition = {
  clientKey: string;
  fieldCode: string;
  operatorCode: string;
  value: string;
};

let draftConditionSequence = 0;

function nextDraftConditionKey(prefix = 'condition'): string {
  draftConditionSequence += 1;
  return `${prefix}-${draftConditionSequence}`;
}

type DraftRule = {
  id: number | null;
  name: string;
  marketScope: ScannerMarketScope;
  universeScope: ScannerUniverseScope;
  enabled: boolean;
  webPushEnabled: boolean;
  conditions: DraftCondition[];
};

const EMPTY_DASHBOARD: ScannerDashboard = {
  rules: [],
  matches: [],
  capabilities: {
    fields: [],
    operators: [],
    markets: ['ALL', 'KOSPI', 'KOSDAQ'],
    matchMode: 'ALL',
  },
};

function blankCondition(dashboard: ScannerDashboard): DraftCondition {
  return {
    clientKey: nextDraftConditionKey('new'),
    fieldCode: dashboard.capabilities.fields[0]?.code || 'CHANGE_RATE',
    operatorCode: dashboard.capabilities.operators[0]?.code || 'GTE',
    value: '',
  };
}

function blankRule(dashboard: ScannerDashboard): DraftRule {
  return {
    id: null,
    name: '',
    marketScope: 'ALL',
    universeScope: 'REALTIME_COVERAGE',
    enabled: true,
    webPushEnabled: true,
    conditions: [blankCondition(dashboard)],
  };
}

function draftFromRule(rule: ScannerRule): DraftRule {
  return {
    id: rule.id,
    name: rule.name,
    marketScope: (['ALL', 'KOSPI', 'KOSDAQ'].includes(rule.marketScope)
      ? rule.marketScope
      : 'ALL') as ScannerMarketScope,
    universeScope: (rule.universeScope === 'STRONG_SECTOR_12'
      ? 'STRONG_SECTOR_12'
      : 'REALTIME_COVERAGE') as ScannerUniverseScope,
    enabled: rule.enabled,
    webPushEnabled: rule.webPushEnabled,
    conditions: rule.conditions.map((condition, index) => ({
      clientKey: condition.id
        ? `rule-${rule.id}-condition-${condition.id}`
        : `rule-${rule.id}-condition-${index}-${nextDraftConditionKey('fallback')}`,
      fieldCode: condition.fieldCode,
      operatorCode: condition.operatorCode,
      value: String(condition.numericValue ?? ''),
    })),
  };
}

function errorText(error: any): string {
  return error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || '요청을 처리하지 못했습니다.';
}

function formatPct(value?: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  const numeric = Number(value);
  return `${numeric > 0 ? '+' : ''}${numeric.toFixed(2)}%`;
}

function formatPrice(value?: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  return Math.round(Number(value)).toLocaleString('ko-KR');
}

function formatMoney(value?: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '-';
  const numeric = Number(value);
  const absolute = Math.abs(numeric);
  const sign = numeric < 0 ? '-' : numeric > 0 ? '+' : '';
  if (absolute >= 1_0000_0000_0000) return `${sign}${(absolute / 1_0000_0000_0000).toFixed(1)}조`;
  if (absolute >= 1_0000_0000) return `${sign}${(absolute / 1_0000_0000).toFixed(0)}억`;
  if (absolute >= 1_0000) return `${sign}${(absolute / 1_0000).toFixed(0)}만`;
  return `${sign}${absolute.toLocaleString('ko-KR')}`;
}

function compactTime(value?: string): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.replace('T', ' ').slice(5, 16);
  return parsed.toLocaleString('ko-KR', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function operatorSymbol(operatorCode?: string): string {
  if (operatorCode === 'GTE' || operatorCode === '>=') return '>=';
  if (operatorCode === 'GT' || operatorCode === '>') return '>';
  if (operatorCode === 'LTE' || operatorCode === '<=') return '<=';
  if (operatorCode === 'LT' || operatorCode === '<') return '<';
  return operatorCode || '?';
}

function formatThreshold(value: number, unit?: string): string {
  if (!Number.isFinite(value)) return '?';
  if (unit === '%') {
    return `${value.toLocaleString('ko-KR', {maximumFractionDigits: 6})}%`;
  }
  if (unit === '배') {
    return `${value.toLocaleString('ko-KR', {maximumFractionDigits: 4})}배`;
  }
  if (unit === '주') {
    return `${value.toLocaleString('ko-KR', {maximumFractionDigits: 0})}주`;
  }

  const absolute = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (absolute >= 1_0000_0000_0000) {
    return `${sign}${(absolute / 1_0000_0000_0000).toLocaleString('ko-KR', {maximumFractionDigits: 3})}조원`;
  }
  if (absolute >= 1_0000_0000) {
    return `${sign}${(absolute / 1_0000_0000).toLocaleString('ko-KR', {maximumFractionDigits: 3})}억원`;
  }
  if (absolute >= 1_0000) {
    return `${sign}${(absolute / 1_0000).toLocaleString('ko-KR', {maximumFractionDigits: 3})}만원`;
  }
  return `${value.toLocaleString('ko-KR', {maximumFractionDigits: 6})}원`;
}

function conditionLabel(condition: ScannerCondition): string {
  return `${condition.fieldLabel || condition.fieldCode} ${operatorSymbol(condition.operatorCode)} ${formatThreshold(Number(condition.numericValue), condition.unit)}`;
}

function draftConditionLabel(condition: DraftCondition, dashboard: ScannerDashboard): string {
  const field = dashboard.capabilities.fields.find((item) => item.code === condition.fieldCode);
  const numericValue = Number(String(condition.value).replace(/,/g, ''));
  const valueText = String(condition.value).trim() && Number.isFinite(numericValue)
    ? formatThreshold(numericValue, field?.unit)
    : '?';
  return `${field?.label || condition.fieldCode} ${operatorSymbol(condition.operatorCode)} ${valueText}`;
}

function queryPreview(draft: DraftRule, dashboard: ScannerDashboard): string {
  const market = draft.marketScope === 'ALL' ? 'ALL' : draft.marketScope;
  const universe = draft.universeScope === 'STRONG_SECTOR_12' ? 'STRONG_SECTOR_12' : 'REALTIME_COVERAGE';
  const expression = draft.conditions.map((condition) => draftConditionLabel(condition, dashboard)).join(' AND ');
  return `${universe} / ${market} WHERE ${expression || '?'}`;
}

function ruleQueryLabel(rule: ScannerRule): string {
  return rule.conditions.map(conditionLabel).join(' AND ');
}


type ScannerActionIconName = 'plus' | 'close' | 'trash';

function ScannerActionIcon({name}: {name: ScannerActionIconName}) {
  if (name === 'plus') {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M10 4v12" />
        <path d="M4 10h12" />
      </svg>
    );
  }
  if (name === 'trash') {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M6 7h8" />
        <path d="M8 7V5.5h4V7" />
        <path d="m7 7 .5 8h5l.5-8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <path d="m6 6 8 8" />
      <path d="m14 6-8 8" />
    </svg>
  );
}

type ScannerMatchRow = ScannerDashboard['matches'][number];

type MyScannerPanelProps = {
  onOpenStock?: (match: ScannerMatchRow) => void;
};

type ScannerMatchGroup = {
  ticker: string;
  representative: ScannerMatchRow;
  ruleNames: string[];
};

function scannerMatchTimestamp(value?: string): number {
  if (!value) return Number.NEGATIVE_INFINITY;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
}

function scannerMatchRuleSummary(ruleNames: string[]): string {
  const primary = ruleNames[0] || '조건 매칭';
  const additionalCount = Math.max(0, ruleNames.length - 1);
  return additionalCount > 0 ? `${primary} 외 ${additionalCount}` : primary;
}

function MyScannerPanel({onOpenStock}: MyScannerPanelProps) {
  const [dashboard, setDashboard] = useState<ScannerDashboard>(EMPTY_DASHBOARD);
  const [draft, setDraft] = useState<DraftRule>(() => blankRule(EMPTY_DASHBOARD));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [ruleListOpen, setRuleListOpen] = useState(false);
  const [expandedCustomRuleIds, setExpandedCustomRuleIds] = useState<Set<number>>(() => new Set());
  const requestRef = useRef<AbortController | null>(null);
  const editorOpenRef = useRef(false);
  const mutatingRef = useRef(false);

  const refresh = useCallback(async (silent = false) => {
    if (silent) {
      if (editorOpenRef.current || mutatingRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      // Background polling never cancels an already-running dashboard request.
      if (requestRef.current) return;
    } else {
      requestRef.current?.abort();
    }

    const controller = new AbortController();
    requestRef.current = controller;
    if (!silent) setLoading(true);
    try {
      const next = await getScannerDashboard(controller.signal);
      if (requestRef.current !== controller) return;
      // Dashboard polling owns server state only. Never overwrite an in-progress editor draft.
      setDashboard(next);
    } catch (error: any) {
      if (error?.name !== 'CanceledError' && error?.name !== 'AbortError' && requestRef.current === controller) {
        setMessage(errorText(error));
      }
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        if (!silent) setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => {
      void refresh(true);
    }, 10_000);
    return () => {
      window.clearInterval(timer);
      requestRef.current?.abort();
    };
  }, [refresh]);

  const activeRules = useMemo(
      () => dashboard.rules.filter((rule) => rule.enabled).length,
      [dashboard.rules],
  );

  const managedRules = useMemo(
      () => dashboard.rules.filter((rule) => rule.managed),
      [dashboard.rules],
  );

  const customRules = useMemo(
      () => dashboard.rules.filter((rule) => !rule.managed),
      [dashboard.rules],
  );

  const groupedMatches = useMemo<ScannerMatchGroup[]>(() => {
    const grouped = new Map<string, {
      ticker: string;
      representative: ScannerMatchRow;
      ruleNames: string[];
      ruleIds: Set<number>;
    }>();

    dashboard.matches.forEach((match, index) => {
      const ticker = String(match.ticker || '').trim();
      const groupKey = ticker || `__missing-ticker-${match.ruleId}-${index}`;
      const current = grouped.get(groupKey);

      if (!current) {
        grouped.set(groupKey, {
          ticker,
          representative: match,
          ruleNames: [match.ruleName || '조건 매칭'],
          ruleIds: new Set([match.ruleId]),
        });
        return;
      }

      if (!current.ruleIds.has(match.ruleId)) {
        current.ruleIds.add(match.ruleId);
        current.ruleNames.push(match.ruleName || '조건 매칭');
      }

      if (scannerMatchTimestamp(match.lastMatchedAt) > scannerMatchTimestamp(current.representative.lastMatchedAt)) {
        current.representative = match;
      }
    });

    return Array.from(grouped.values()).map(({ruleIds: _ruleIds, ...group}) => group);
  }, [dashboard.matches]);

  const startCreate = useCallback(() => {
    editorOpenRef.current = true;
    setDraft(blankRule(dashboard));
    setEditorOpen(true);
    setMessage('');
  }, [dashboard]);

  const editRule = useCallback((rule: ScannerRule) => {
    if (rule.managed) {
      setMessage('기본 스캐너는 운영자가 관리하며 활성/비활성만 변경할 수 있습니다.');
      return;
    }
    editorOpenRef.current = true;
    setDraft(draftFromRule(rule));
    setEditorOpen(true);
    setMessage('');
  }, []);

  const toggleRuleDetails = useCallback((rule: ScannerRule) => {
    // Managed presets are intentionally title + state only. Their conditions are fixed by the
    // operator, so opening a large detail panel only pushes scanner matches down the viewport.
    if (rule.managed) return;
    setExpandedCustomRuleIds((current) => {
      const next = new Set(current);
      if (next.has(rule.id)) next.delete(rule.id);
      else next.add(rule.id);
      return next;
    });
  }, []);

  const closeEditor = useCallback(() => {
    editorOpenRef.current = false;
    setEditorOpen(false);
    setDraft(blankRule(dashboard));
    void refresh(true);
  }, [dashboard, refresh]);

  const updateCondition = useCallback((index: number, patch: Partial<DraftCondition>) => {
    setDraft((current) => ({
      ...current,
      conditions: current.conditions.map((condition, rowIndex) => (
        rowIndex === index ? {...condition, ...patch} : condition
      )),
    }));
  }, []);

  const addCondition = useCallback(() => {
    setDraft((current) => current.conditions.length >= 12 ? current : ({
      ...current,
      conditions: [...current.conditions, blankCondition(dashboard)],
    }));
  }, [dashboard]);

  const removeCondition = useCallback((index: number) => {
    setDraft((current) => ({
      ...current,
      conditions: current.conditions.filter((_, rowIndex) => rowIndex !== index),
    }));
  }, []);

  const save = useCallback(async () => {
    if (mutatingRef.current) return;
    const name = draft.name.trim();
    if (!name) {
      setMessage('스캐너 이름을 입력해 주세요.');
      return;
    }
    if (!draft.conditions.length) {
      setMessage('조건을 하나 이상 추가해 주세요.');
      return;
    }

    const parsedConditions = draft.conditions.map((condition) => {
      const rawValue = String(condition.value).replace(/,/g, '').trim();
      return {
        fieldCode: condition.fieldCode,
        operatorCode: condition.operatorCode,
        rawValue,
        numericValue: rawValue === '' ? Number.NaN : Number(rawValue),
      };
    });
    if (parsedConditions.some((condition) => (
      condition.rawValue === '' || !Number.isFinite(condition.numericValue)
    ))) {
      setMessage('모든 조건에 숫자 값을 입력해 주세요.');
      return;
    }

    const payload: ScannerRulePayload = {
      name,
      marketScope: draft.marketScope,
      universeScope: draft.universeScope,
      enabled: draft.enabled,
      webPushEnabled: draft.webPushEnabled,
      conditions: parsedConditions.map(({rawValue: _rawValue, ...condition}) => condition),
    };

    requestRef.current?.abort();
    mutatingRef.current = true;
    setSaving(true);
    try {
      const savedRule = draft.id === null
        ? await createScannerRule(payload)
        : await updateScannerRule(draft.id, payload);

      // Mutation response is authoritative for the edited rule. Apply it locally first so the
      // editor does not collapse into a GET-driven flash, and clear stale matches because the
      // backend intentionally resets match episodes after create/edit.
      setDashboard((current) => ({
        ...current,
        rules: [savedRule, ...current.rules.filter((rule) => rule.id !== savedRule.id)],
        matches: current.matches.filter((match) => match.ruleId !== savedRule.id),
      }));
      editorOpenRef.current = false;
      setEditorOpen(false);
      setDraft(blankRule(dashboard));
      // Successful save is already visible in the rule row. Avoid inserting a success banner that
      // shifts the entire scanner workspace and looks like a flash.
      setMessage('');
    } catch (error: any) {
      setMessage(errorText(error));
    } finally {
      mutatingRef.current = false;
      setSaving(false);
    }
  }, [dashboard, draft]);

  const toggleRule = useCallback(async (rule: ScannerRule) => {
    if (mutatingRef.current) return;
    requestRef.current?.abort();
    mutatingRef.current = true;
    try {
      const updatedRule = await setScannerRuleEnabled(rule.id, !rule.enabled);
      setDashboard((current) => ({
        ...current,
        rules: current.rules.map((item) => item.id === updatedRule.id ? updatedRule : item),
        matches: current.matches.filter((match) => match.ruleId !== updatedRule.id),
      }));
      // The editor does not expose enabled directly, but save payload contains it. Keep only this
      // server-owned flag synchronized so a later save cannot accidentally undo the toggle.
      setDraft((current) => current.id === updatedRule.id
        ? {...current, enabled: updatedRule.enabled}
        : current);
    } catch (error: any) {
      setMessage(errorText(error));
    } finally {
      mutatingRef.current = false;
    }
  }, []);

  const removeRule = useCallback(async (rule: ScannerRule) => {
    if (mutatingRef.current) return;
    if (rule.managed) {
      setMessage('기본 스캐너는 운영자가 관리하므로 삭제할 수 없습니다.');
      return;
    }
    if (!window.confirm(`'${rule.name}' 스캐너를 삭제할까요?`)) return;
    requestRef.current?.abort();
    mutatingRef.current = true;
    try {
      await deleteScannerRule(rule.id);
      setDashboard((current) => ({
        ...current,
        rules: current.rules.filter((item) => item.id !== rule.id),
        matches: current.matches.filter((match) => match.ruleId !== rule.id),
      }));
      setExpandedCustomRuleIds((current) => {
        if (!current.has(rule.id)) return current;
        const next = new Set(current);
        next.delete(rule.id);
        return next;
      });
      if (draft.id === rule.id) {
        editorOpenRef.current = false;
        setDraft(blankRule(dashboard));
        setEditorOpen(false);
      }
    } catch (error: any) {
      setMessage(errorText(error));
    } finally {
      mutatingRef.current = false;
    }
  }, [dashboard, draft.id]);

  const renderRuleRow = (rule: ScannerRule) => {
    const expanded = !rule.managed && expandedCustomRuleIds.has(rule.id);
    const ruleSummary = ruleQueryLabel(rule);
    return (
      <article
        key={rule.id}
        className={[
          !rule.enabled ? 'is-disabled' : 'is-active',
          rule.managed ? 'is-managed' : 'is-custom',
          expanded ? 'is-expanded' : '',
          editorOpen && draft.id === rule.id ? 'is-editing' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="live-summary-scanner__rule-head">
          {rule.managed ? (
            <div className="is-rule is-static" title={ruleSummary}>
              <span className="live-summary-scanner__rule-title">
                <strong>{rule.name}</strong>
                <em>기본</em>
              </span>
            </div>
          ) : (
            <button
              type="button"
              className="is-rule"
              onClick={() => toggleRuleDetails(rule)}
              aria-expanded={expanded}
              aria-label={`${rule.name} 조건 ${expanded ? '접기' : '보기'}`}
              title={expanded ? '상세 조건 접기' : '상세 조건 보기'}
            >
              <span className="live-summary-scanner__rule-title">
                <strong>{rule.name}</strong>
              </span>
              <span className={`live-summary-scanner__chevron ${expanded ? 'is-open' : ''}`} aria-hidden="true">⌄</span>
            </button>
          )}
          <button
            type="button"
            className={`live-summary-scanner__state-button ${rule.enabled ? 'is-on' : 'is-off'}`}
            onClick={() => void toggleRule(rule)}
            aria-pressed={rule.enabled}
            aria-label={`${rule.name} ${rule.enabled ? '비활성화' : '활성화'}`}
            title={rule.enabled ? '감시 비활성화' : '감시 활성화'}
          >
            <i aria-hidden="true" />
            {rule.enabled ? '활성' : '비활성'}
          </button>
        </div>

        {expanded && (
          <div className="live-summary-scanner__rule-detail">
            <div className="live-summary-scanner__rule-meta">
              <span>
                {rule.marketScope === 'ALL' ? '전체시장' : rule.marketScope}
                {' · '}
                {rule.universeScope === 'STRONG_SECTOR_12' ? '상대강도 상위 12섹터' : '전체 실시간 후보'}
              </span>
            </div>
            <div className="live-summary-scanner__rule-body">
              <code>{ruleSummary}</code>
            </div>
            <div className="live-summary-scanner__rule-foot">
              <span>{rule.lastMatchCount || 0} 매칭 · {rule.lastCandidateCount || 0} 평가</span>
              <span className={`live-summary-scanner__push-state ${rule.webPushEnabled ? 'is-on' : 'is-off'}`}>
                <i aria-hidden="true" />
                {rule.webPushEnabled ? 'Push ON' : 'Push OFF'}
              </span>
              <span>{compactTime(rule.lastEvaluatedAt)}</span>
              <div className="live-summary-scanner__rule-actions">
                <button type="button" className="is-edit" onClick={() => editRule(rule)}>수정</button>
                <button
                  type="button"
                  className="is-delete"
                  onClick={() => void removeRule(rule)}
                  aria-label={`${rule.name} 삭제`}
                  title="삭제"
                >
                  <ScannerActionIcon name="trash" />
                </button>
              </div>
            </div>
          </div>
        )}
      </article>
    );
  };

  return (
    <div className="live-summary-scanner is-modern-scanner-v1">
      <div className="live-summary-scanner__toolbar">
        <div className="live-summary-scanner__status">
          <b>{activeRules}</b><span>개 감시</span>
          <i />
          <b>{customRules.length}</b><span>개 직접 등록</span>
          <i />
          <b>{groupedMatches.length}</b><span>개 현재 매칭</span>
        </div>
        <div className="live-summary-scanner__toolbar-actions">
          {dashboard.rules.length > 0 && (
            <button
              type="button"
              className={`is-list-toggle ${ruleListOpen ? 'is-open' : ''}`}
              onClick={() => setRuleListOpen((current) => !current)}
              aria-expanded={ruleListOpen}
              aria-controls="policyintel-scanner-rule-list"
              title={ruleListOpen ? '스캐너 목록 접기' : '스캐너 목록 보기'}
            >
              <span>스캐너 {dashboard.rules.length}</span>
              <span className="live-summary-scanner__list-chevron" aria-hidden="true">⌄</span>
            </button>
          )}
          <button type="button" className="is-create" onClick={startCreate} aria-label="새 조건" title="새 조건">
            <ScannerActionIcon name="plus" />
            <span>조건</span>
          </button>
        </div>
      </div>

      {message && <div className="live-summary-scanner__message">{message}</div>}

      {editorOpen && (
        <section className="live-summary-scanner__editor">
          <div className="live-summary-scanner__editor-top">
            <input
              value={draft.name}
              onChange={(event) => setDraft((current) => ({...current, name: event.target.value}))}
              placeholder="스캐너 이름"
              maxLength={100}
            />
            <div className="live-summary-scanner__market-switch is-market" role="group" aria-label="시장 범위">
              {(['ALL', 'KOSPI', 'KOSDAQ'] as ScannerMarketScope[]).map((market) => (
                <button
                  type="button"
                  key={market}
                  className={draft.marketScope === market ? 'is-active' : ''}
                  aria-pressed={draft.marketScope === market}
                  onClick={() => setDraft((current) => ({...current, marketScope: market}))}
                >
                  {market === 'ALL' ? '전체' : market}
                </button>
              ))}
            </div>
          </div>

          <div className="live-summary-scanner__market-switch is-universe" role="group" aria-label="스캔 범위">
            {([
              ['REALTIME_COVERAGE', '전체 실시간 후보'],
              ['STRONG_SECTOR_12', '상대강도 상위 12섹터'],
            ] as Array<[ScannerUniverseScope, string]>).map(([scope, label]) => (
              <button
                type="button"
                key={scope}
                className={draft.universeScope === scope ? 'is-active' : ''}
                aria-pressed={draft.universeScope === scope}
                onClick={() => setDraft((current) => ({...current, universeScope: scope}))}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="live-summary-scanner__condition-list">
            {draft.conditions.map((condition, index) => (
              <div className="live-summary-scanner__condition" key={condition.clientKey}>
                <select
                  value={condition.fieldCode}
                  onChange={(event) => updateCondition(index, {fieldCode: event.target.value})}
                >
                  {dashboard.capabilities.fields.map((field) => (
                    <option value={field.code} key={field.code}>{field.label}</option>
                  ))}
                </select>
                <select
                  value={condition.operatorCode}
                  onChange={(event) => updateCondition(index, {operatorCode: event.target.value})}
                >
                  {dashboard.capabilities.operators.map((operator) => (
                    <option value={operator.code} key={operator.code}>
                      {operator.symbol || operatorSymbol(operator.code)} {operator.description || ''}
                    </option>
                  ))}
                </select>
                <input
                  inputMode="decimal"
                  value={condition.value}
                  onChange={(event) => updateCondition(index, {value: event.target.value})}
                  placeholder="값"
                />
                <span>
                  {(() => {
                    const unit = dashboard.capabilities.fields
                      .find((field) => field.code === condition.fieldCode)?.unit;
                    return unit === 'KRW' ? '원' : unit || '';
                  })()}
                </span>
                <button
                  type="button"
                  className="is-remove"
                  onClick={() => removeCondition(index)}
                  disabled={draft.conditions.length <= 1}
                  aria-label="조건 삭제"
                ><ScannerActionIcon name="close" /></button>
              </div>
            ))}
          </div>

          <div className="live-summary-scanner__query-preview" aria-label="스캐너 조건 쿼리 미리보기">
            <code>{queryPreview(draft, dashboard)}</code>
          </div>

          <div className="live-summary-scanner__editor-actions">
            <button
              type="button"
              className="is-add"
              onClick={addCondition}
              disabled={draft.conditions.length >= 12}
              aria-label="조건 추가"
              title="조건 추가"
            >
              <ScannerActionIcon name="plus" />
            </button>
            <label className={`live-summary-scanner__push-control ${draft.webPushEnabled ? 'is-on' : 'is-off'}`}>
              <input
                type="checkbox"
                checked={draft.webPushEnabled}
                onChange={(event) => setDraft((current) => ({...current, webPushEnabled: event.target.checked}))}
                aria-label={`Web Push ${draft.webPushEnabled ? '활성화됨' : '비활성화됨'}`}
              />
              <span>Web Push</span>
              <strong>{draft.webPushEnabled ? 'ON' : 'OFF'}</strong>
            </label>
            <div className="live-summary-scanner__editor-submit">
              <button type="button" onClick={closeEditor}>취소</button>
              <button type="button" className="is-primary" onClick={save} disabled={saving}>
                {saving ? '저장 중' : '저장'}
              </button>
            </div>
          </div>
        </section>
      )}

      <div className={`live-summary-scanner__workspace ${ruleListOpen ? 'is-rule-list-open' : 'is-rule-list-closed'}`}>
        {ruleListOpen && (
          <section className="live-summary-scanner__rules" id="policyintel-scanner-rule-list">
            {loading && dashboard.rules.length === 0 ? (
              <p className="live-summary-empty">스캐너를 불러오는 중입니다.</p>
            ) : dashboard.rules.length === 0 ? (
              <button type="button" className="live-summary-scanner__empty-create" onClick={startCreate}>
                첫 조건 만들기
              </button>
            ) : (
              <div className="live-summary-scanner__rule-groups">
                {managedRules.length > 0 && (
                  <div className="live-summary-scanner__rule-group is-managed-group">
                    <div className="live-summary-scanner__rule-group-label">
                      <strong>기본 스캐너</strong>
                      <span>활성/비활성만 선택</span>
                    </div>
                    <div className="live-summary-scanner__rule-grid">
                      {managedRules.map(renderRuleRow)}
                    </div>
                  </div>
                )}
                {customRules.length > 0 && (
                  <div className="live-summary-scanner__rule-group is-custom-group">
                    <div className="live-summary-scanner__rule-group-label">
                      <strong>내 스캐너</strong>
                      <span>제목 클릭 시 상세 보기</span>
                    </div>
                    <div className="live-summary-scanner__rule-grid">
                      {customRules.map(renderRuleRow)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        <section className="live-summary-scanner__matches is-decision-list is-discovery-list">
          {groupedMatches.length ? groupedMatches.map((group) => {
            const match = group.representative;
            const ruleSummary = scannerMatchRuleSummary(group.ruleNames);
            const stockName = match.name || match.ticker;
            const allRuleNames = group.ruleNames.join(', ');
            const sectorLabel = match.sectorName || '업종 확인 중';
            const marketLabel = match.market || '시장 확인 중';
            const matchedAt = compactTime(match.lastMatchedAt);

            return (
              <button
                type="button"
                className="live-summary-scanner__match-card is-holding-aligned"
                key={group.ticker || `${match.ruleId}-${match.ticker}`}
                onClick={() => onOpenStock?.(match)}
                disabled={!onOpenStock}
                title={onOpenStock
                  ? `${stockName} 종목 상세 열기 · 매칭 조건: ${allRuleNames}`
                  : `매칭 조건: ${allRuleNames}`}
              >
                <div className="live-summary-compact-row">
                  <div className="live-summary-compact-row__identity">
                    <div className="live-summary-holding-name-line">
                      <b>{stockName}</b>
                      <em className="is-scanner">스캐너</em>
                    </div>
                    <small>{match.ticker} · {sectorLabel}</small>
                  </div>

                  <div className="live-summary-compact-row__right">
                    <div className="live-summary-compact-row__quote">
                      <strong>{formatPrice(match.price)}원</strong>
                      <em className={match.changeRate === null || match.changeRate === undefined
                        ? 'is-neutral'
                        : Number(match.changeRate) >= 0 ? 'is-up' : 'is-down'}>
                        {formatPct(match.changeRate)}
                      </em>
                    </div>
                  </div>

                  <div className="live-summary-holding-decision-line is-thesis is-judgment-contract is-discovery-contract is-scanner-contract">
                    <strong className="is-good">스캐너 조건 진입</strong>
                    <em>{ruleSummary}</em>
                    <small>{ruleSummary} 조건 매칭 · {matchedAt}</small>

                    <span className="live-summary-holding-thesis-counts is-discovery-facts">
                      <i>거래대금 {formatMoney(match.tradingValue)}</i>
                      <i>업종 {sectorLabel}</i>
                    </span>

                    <span className="live-summary-holding-compact-factors">
                      <i>외인+기관 · {formatMoney(match.smartMoneyNetBuyAmount)}</i>
                      <i>프로그램 · {formatMoney(match.programNetBuyExecAmount)}</i>
                      <i>시장 · {marketLabel}</i>
                      <i>스캐너 · {ruleSummary}</i>
                      {match.strongSectorMember && match.sectorName && (
                        <i>업종 강도 · {formatPct(match.sectorChangeRate)} · {match.sectorRank || '-'}위</i>
                      )}
                    </span>
                  </div>
                </div>
              </button>
            );
          }) : (
            <p className="live-summary-empty">현재 조건에 들어온 종목이 없습니다.</p>
          )}
        </section>
      </div>

      {dashboard.capabilities.coverageNote && (
        <p className="live-summary-scanner__coverage">{dashboard.capabilities.coverageNote}</p>
      )}
    </div>
  );
}

export default React.memo(MyScannerPanel);
